import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useAuth } from '@/hooks/useAuth';
import { AuthApi } from '@/api';
import { KeyRound, ShieldCheck, ShieldOff, Copy, Check, Eye, EyeOff } from 'lucide-react';

const TABS = [
  { id: 'password', label: 'Change Password', icon: KeyRound },
  { id: 'mfa', label: 'Two-Factor Auth', icon: ShieldCheck },
];

export default function Account() {
  const { push } = useToast();
  const { user, refresh: refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('password');

  // Change password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // MFA state
  const [mfaStep, setMfaStep] = useState<'idle' | 'scanning'>('idle');
  const [mfaData, setMfaData] = useState<{ secret: string; qrDataUrl: string } | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [copied, setCopied] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');

  const passwordMutation = useMutation({
    mutationFn: () => AuthApi.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      push({ tone: 'success', title: 'Password changed' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (err: any) => push({ tone: 'error', title: 'Failed to change password', body: err.message }),
  });

  const enrollMutation = useMutation({
    mutationFn: () => AuthApi.enrollMfa(),
    onSuccess: (data) => {
      setMfaData({ secret: data.secret, qrDataUrl: data.qrDataUrl });
      setMfaStep('scanning');
      setMfaCode('');
    },
    onError: (err: any) => push({ tone: 'error', title: 'Failed to start MFA setup', body: err.message }),
  });

  const verifyMutation = useMutation({
    mutationFn: (code: string) => AuthApi.verifyMfa(code),
    onSuccess: async () => {
      await refreshUser();
      setMfaStep('idle');
      setMfaData(null);
      setMfaCode('');
      push({ tone: 'success', title: 'MFA enabled', body: 'Your account is now protected with two-factor authentication.' });
    },
    onError: (err: any) => push({ tone: 'error', title: 'Invalid code', body: err.message }),
  });

  const disableMutation = useMutation({
    mutationFn: (password: string) => AuthApi.disableMfa(password),
    onSuccess: async () => {
      await refreshUser();
      setShowDisableModal(false);
      setDisablePassword('');
      push({ tone: 'success', title: 'MFA disabled' });
    },
    onError: (err: any) => push({ tone: 'error', title: 'Failed to disable MFA', body: err.message }),
  });

  const copySecret = () => {
    if (!mfaData) return;
    navigator.clipboard.writeText(mfaData.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const passwordValid =
    currentPassword.length >= 8 &&
    newPassword.length >= 8 &&
    newPassword === confirmPassword;

  return (
    <div className="flex flex-col md:h-[calc(100vh-48px)]">
      <PageHeader title="Account" subtitle="Manage your password and security settings" />

      <div className="flex flex-col lg:flex-row gap-5 flex-1 min-h-0 mt-5">
        {/* Sidebar — horizontal scroll row on mobile, vertical rail on desktop */}
        <div className="w-full lg:w-44 shrink-0 lg:sticky lg:top-0">
          <nav className="flex lg:flex-col gap-1 lg:gap-0.5 lg:space-y-0.5 overflow-x-auto">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`shrink-0 lg:w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left whitespace-nowrap ${
                    active ? 'bg-rdx/8 text-rdx' : 'text-muted hover:bg-slate-100 hover:text-ink'
                  }`}
                  style={active ? { backgroundColor: 'rgba(179,38,30,0.08)' } : undefined}
                >
                  <Icon size={15} />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 overflow-y-auto pb-16">

          {activeTab === 'password' && (
            <div className="card card-pad space-y-4 max-w-md">
              <div>
                <h3 className="text-sm font-semibold text-ink">Change Password</h3>
                <p className="text-xs text-muted mt-0.5">Use a strong password of at least 8 characters</p>
              </div>
              <div>
                <label className="label">Current password</label>
                <div className="relative mt-1">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    className="input pr-9"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                    onClick={() => setShowCurrent((v) => !v)}
                  >
                    {showCurrent ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="label">New password</label>
                <div className="relative mt-1">
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="input pr-9"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted hover:text-ink"
                    onClick={() => setShowNew((v) => !v)}
                  >
                    {showNew ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="label">Confirm new password</label>
                <input
                  type="password"
                  className={`input mt-1 ${confirmPassword && newPassword !== confirmPassword ? 'input-error' : ''}`}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="new-password"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-rdx mt-1">Passwords do not match</p>
                )}
              </div>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => passwordMutation.mutate()}
                disabled={!passwordValid || passwordMutation.isPending}
              >
                {passwordMutation.isPending ? 'Saving…' : 'Update password'}
              </button>
            </div>
          )}

          {activeTab === 'mfa' && (
            <div className="space-y-4 max-w-lg">
              <div className="card card-pad space-y-4">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Two-Factor Authentication</h3>
                  <p className="text-xs text-muted mt-0.5">Add an extra layer of security using an authenticator app like Google Authenticator</p>
                </div>

                {user?.totp_enabled ? (
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-emerald-50 border border-emerald-200">
                    <ShieldCheck size={20} className="text-emerald-600 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-emerald-800">MFA is enabled</p>
                      <p className="text-xs text-emerald-700 mt-0.5">Your account is protected with a time-based one-time password.</p>
                    </div>
                    <button
                      className="btn btn-sm btn-danger shrink-0"
                      onClick={() => { setShowDisableModal(true); setDisablePassword(''); }}
                    >
                      <ShieldOff size={13} />
                      Disable MFA
                    </button>
                  </div>
                ) : mfaStep === 'idle' ? (
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-slate-50 border border-line">
                    <ShieldOff size={20} className="text-muted mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-ink">MFA is not enabled</p>
                      <p className="text-xs text-muted mt-0.5">Enable two-factor authentication to secure your account.</p>
                    </div>
                    <button
                      className="btn btn-sm btn-primary shrink-0"
                      onClick={() => enrollMutation.mutate()}
                      disabled={enrollMutation.isPending}
                    >
                      <ShieldCheck size={13} />
                      {enrollMutation.isPending ? 'Setting up…' : 'Enable MFA'}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="flex items-start gap-4 p-4 rounded-lg bg-amber-50 border border-amber-200">
                      <div className="text-amber-600 text-lg font-bold shrink-0">!</div>
                      <p className="text-xs text-amber-800">Save your secret key somewhere safe. If you lose access to your authenticator app, you will need it to recover your account.</p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-rdx text-white text-xs font-bold grid place-items-center shrink-0">1</span>
                        <p className="text-sm font-medium text-ink">Scan this QR code with Google Authenticator</p>
                      </div>
                      {mfaData && (
                        <div className="flex gap-6 items-start pl-7">
                          <img src={mfaData.qrDataUrl} alt="MFA QR code" className="w-40 h-40 border border-line rounded-lg" />
                          <div className="space-y-2">
                            <p className="text-xs text-muted">Or enter this key manually:</p>
                            <div className="flex items-center gap-2">
                              <code className="text-xs font-mono bg-slate-100 px-2 py-1.5 rounded border border-line tracking-widest select-all">
                                {mfaData.secret}
                              </code>
                              <button className="btn btn-sm btn-secondary p-1.5" onClick={copySecret} title="Copy secret">
                                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="w-5 h-5 rounded-full bg-rdx text-white text-xs font-bold grid place-items-center shrink-0">2</span>
                        <p className="text-sm font-medium text-ink">Enter the 6-digit code shown in the app</p>
                      </div>
                      <div className="flex gap-3 items-center pl-7">
                        <input
                          type="text"
                          inputMode="numeric"
                          maxLength={6}
                          className="input w-36 font-mono tracking-widest text-center text-lg"
                          placeholder="000000"
                          value={mfaCode}
                          onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
                          onKeyDown={(e) => e.key === 'Enter' && mfaCode.length === 6 && verifyMutation.mutate(mfaCode)}
                        />
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => verifyMutation.mutate(mfaCode)}
                          disabled={mfaCode.length !== 6 || verifyMutation.isPending}
                        >
                          {verifyMutation.isPending ? 'Verifying…' : 'Verify & Activate'}
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => { setMfaStep('idle'); setMfaData(null); setMfaCode(''); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Disable MFA modal */}
      {showDisableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="card card-pad w-[380px] space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-red-100 grid place-items-center shrink-0">
                <ShieldOff size={18} className="text-rdx" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-ink">Disable two-factor authentication</h3>
                <p className="text-xs text-muted mt-0.5">Confirm your password to continue</p>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-800">Disabling MFA will make your account less secure. Anyone with your password will be able to sign in without a second factor.</p>
            </div>
            <div>
              <label className="label">Current password</label>
              <input
                type="password"
                className="input mt-1"
                autoFocus
                value={disablePassword}
                onChange={(e) => setDisablePassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && disablePassword && disableMutation.mutate(disablePassword)}
                placeholder="Enter your password"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => { setShowDisableModal(false); setDisablePassword(''); }}
                disabled={disableMutation.isPending}
              >
                Cancel
              </button>
              <button
                className="btn btn-sm btn-danger"
                onClick={() => disableMutation.mutate(disablePassword)}
                disabled={!disablePassword || disableMutation.isPending}
              >
                {disableMutation.isPending ? 'Disabling…' : 'Disable MFA'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
